// Apolo IDE — ThinkingBlock.
//
// The LLM's reasoning, collapsed to a single small line: an animated Apolo
// mark (a cosmic orbit — gold satellite circling a blue core, the motion-design
// "thinking" loop) + a phrase ("Pensando…" → "Pensou em N tokens"). Click to
// expand the full chain of thought. Renders small + left-aligned below the
// message text (see StreamingMessage). Motion freezes when thinking finishes
// and under prefers-reduced-motion.

import { useId, useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { useReducedMotion } from 'framer-motion';

// Small inline Apolo mark. Animates via SMIL while `active`; static otherwise.
// There is no Apolo SVG asset (only PNGs), so this is a purpose-built mark in
// the brand's cosmic blue/gold.
function ApoloMark({ active }) {
  const reduce = useReducedMotion();
  const moving = active && !reduce;
  // pos-s5 calm — the mark is blue WHILE thinking (live), but greys out to the
  // muted text color (currentColor, inherited from the .text-apolo-textTertiary
  // button) once finished, so the resting "Pensou em N tokens" reads quiet.
  const tint = active ? 'var(--apolo-accent)' : 'currentColor';
  const dot = active ? 'var(--apolo-highlight)' : 'currentColor';
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true" className="shrink-0">
      <circle cx="8" cy="8" r="6" fill="none" stroke={tint} strokeOpacity="0.3" strokeWidth="1.1" />
      <g>
        {moving && (
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 8 8"
            to="360 8 8"
            dur="1.7s"
            repeatCount="indefinite"
          />
        )}
        <circle cx="8" cy="1.9" r="1.5" fill={dot} />
      </g>
      <circle cx="8" cy="8" r="2.2" fill={tint}>
        {moving && <animate attributeName="opacity" values="0.6;1;0.6" dur="1.7s" repeatCount="indefinite" />}
        {moving && <animate attributeName="r" values="2;2.6;2" dur="1.7s" repeatCount="indefinite" />}
      </circle>
    </svg>
  );
}

export default function ThinkingBlock({ content, finished = false, tokens = 0 }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  if (!content && !finished) return null;
  const label = finished
    ? tokens > 0
      ? `Pensou em ${tokens} tokens`
      : 'Pensou'
    : 'Pensando…';
  return (
    <div className="my-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        className="inline-flex items-center gap-1.5 rounded text-[11px] italic text-apolo-textTertiary transition hover:text-apolo-textSecondary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/40"
      >
        <ApoloMark active={!finished} />
        <span>{label}</span>
        {open ? (
          <ChevronDown size={11} aria-hidden="true" />
        ) : (
          <ChevronRight size={11} aria-hidden="true" />
        )}
      </button>
      {open && (
        <pre
          id={panelId}
          className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md border border-apolo-borderSubtle bg-apolo-surface2 p-2 text-[11px] italic text-apolo-textTertiary"
        >
          {content}
        </pre>
      )}
    </div>
  );
}
